import { useState, useEffect, useRef } from 'react';
import { css, cx, etiColors } from '@eti/styles';
import PropTypes from 'prop-types';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const paddedStyles = css`
  padding: 1.25rem 0;
`;
export const borderStyles = css`
  border-top: 1px dotted ${etiColors.grayScale[6]};

  &:last-child {
    border-bottom: 1px dotted ${etiColors.grayScale[6]};
  }
`;
const backgroundImg = backgroundImage => css`
  background: url(${backgroundImage}) no-repeat center;
  background-size: cover;
  height: 86%;
  left: 0;
  max-height: 512px;
  position: absolute;
  right: 0;
  top: 0;
`;
export const pageWrapperStyles = (backgroundColor, backgroundImage, isBordered, isPadded, shouldShowHalfBackground) => css`
  ${isPadded && paddedStyles};
  ${isBordered && borderStyles};
  ${backgroundColor && `background-color: ${backgroundColor}`};
  ${backgroundImage && !shouldShowHalfBackground && `background: url(${backgroundImage}) no-repeat center;
    background-size: cover;`}
  ${shouldShowHalfBackground && 'position: relative;'};
`;
const mediaQueryMatches = mediaQuery => window.matchMedia(`(${mediaQuery})`).matches;
const getImageSize = () => {
  if (mediaQueryMatches('min-width: 1200px')) {
    return '1600';
  }
  if (mediaQueryMatches('min-width: 375px')) {
    return '1200';
  }
  return '800';
};
export const fallbackImageIataCodes = ['lon', 'sto', 'ist', 'bkk', 'cph', 'par', 'lis', 'rom', 'prg', 'ath'];
const getFallBackImage = () => {
  const randomIataCode = fallbackImageIataCodes[Math.floor(Math.random() * fallbackImageIataCodes.length)];
  return `loc-${randomIataCode}`;
};
const preloadImage = (src, wrapperRef, callback) => {
  const img = new Image();
  img.src = src;
  img.style.display = 'none';
  img.dataset.testid = 'panel-background-image';
  if (wrapperRef.current) {
    const el = wrapperRef.current;
    el.innerHTML = '';
    el.appendChild(img);
  }
  if (typeof callback === 'function') {
    img.onload = () => callback(src);
  }
};
const Panel = _ref => {
  let {
    backgroundColor,
    backgroundImage,
    children,
    className = '',
    customRef,
    dataTestId,
    defaultBackgroundImage,
    id,
    isBordered,
    isPadded = true,
    shouldShowHalfBackground = false
  } = _ref;
  const imageCacheRef = useRef(null);
  const [image, setImage] = useState(null);
  const isInitialPageLoadRef = useRef(true);
  useEffect(() => {
    const fetchImage = async imageArg => {
      let imageSrc;
      if (imageArg.includes('loc')) {
        imageSrc = await import(/* webpackMode: "lazy-once" */`@eti/assets/images/panel/locations/${imageArg}_${getImageSize()}.webp`);
        return preloadImage(imageSrc.default, imageCacheRef, setImage);
      }
      imageSrc = await import(/* webpackMode: "lazy-once" */`@eti/assets/images/panel/${imageArg}_${getImageSize()}.webp`);
      return preloadImage(imageSrc.default, imageCacheRef, setImage);
    };
    (async () => {
      if (backgroundImage || defaultBackgroundImage) {
        try {
          await fetchImage(backgroundImage);
        } catch (err) {
          try {
            await fetchImage(defaultBackgroundImage);
          } catch (error) {
            if (isInitialPageLoadRef.current) {
              await fetchImage(getFallBackImage());
            }
          }
        }
        isInitialPageLoadRef.current = false;
      }
    })();
  }, [backgroundImage, defaultBackgroundImage, image]);
  return /*#__PURE__*/_jsxs("section", {
    className: cx(pageWrapperStyles(backgroundColor, image, isBordered, isPadded, shouldShowHalfBackground), className),
    "data-testid": dataTestId,
    id: id,
    ref: customRef,
    children: [shouldShowHalfBackground && /*#__PURE__*/_jsx("div", {
      className: backgroundImg(image),
      "data-testid": "halfBackgroundImage"
    }), children, /*#__PURE__*/_jsx("div", {
      ref: imageCacheRef
    })]
  });
};
Panel.propTypes = {
  backgroundColor: PropTypes.string,
  backgroundImage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  children: PropTypes.node,
  className: PropTypes.string,
  customRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  dataTestId: PropTypes.string,
  defaultBackgroundImage: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  id: PropTypes.string,
  isBordered: PropTypes.bool,
  isPadded: PropTypes.bool,
  shouldShowHalfBackground: PropTypes.bool
};
export default Panel;